// Контекст тура: его умеет позвать и кнопка в полосе, и форма регистрации.
//
// Провайдер стоит выше входа (см. App.tsx) — именно поэтому окно регистрации
// может запустить тур сразу после того, как аккаунт создан. Сам тур живёт
// здесь же: он уезжает порталом в body и от места в дереве не зависит.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createStorage, type StorageAdapter } from '../store';
import { OnboardingTour } from './OnboardingTour';

/**
 * Тур уже открывали в этом браузере. Настройка вида, а не часть поездки:
 * лежит отдельным ключом рядом с языком и раскладкой панелей и на сервер
 * не уезжает.
 */
export const ONBOARDING_STORAGE_KEY = 'plan-flights.onboarding.v1';

export interface Onboarding {
  /** Открыт ли тур сейчас — кнопке нужно для aria-expanded. */
  open: boolean;
  /**
   * Тур уже открывали. Пока нет — кнопка сама зовёт себя пульсацией; после
   * первого открытия зовёт один раз и навсегда замолкает.
   */
  seen: boolean;
  start(): void;
  close(): void;
}

const OnboardingContext = createContext<Onboarding | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }): ReactElement {
  const [storage] = useState<StorageAdapter>(() => createStorage());
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => storage.get(ONBOARDING_STORAGE_KEY) === 'seen');

  // Отметка ставится на открытии, а не на последнем шаге: приглашение своё
  // дело сделало, и звать второй раз того, кто тур закрыл, — навязчиво.
  const start = useCallback(() => {
    setOpen(true);
    setSeen(true);
    storage.set(ONBOARDING_STORAGE_KEY, 'seen');
  }, [storage]);

  const close = useCallback(() => setOpen(false), []);

  const value = useMemo<Onboarding>(
    () => ({ open, seen, start, close }),
    [open, seen, start, close],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {open && <OnboardingTour onClose={close} />}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): Onboarding {
  const ctx = useContext(OnboardingContext);
  if (ctx === undefined) {
    throw new Error('useOnboarding() должен использоваться внутри <OnboardingProvider>');
  }
  return ctx;
}
